/**
 * Pure calculator logic — no React, no server imports.
 */

// ── Project types ──

export const PROJECT_TYPES = [
	{
		value: 'home-cleanout',
		label: 'Home Cleanout',
		recommendedSizes: [15, 20],
	},
	{
		value: 'kitchen-remodel',
		label: 'Kitchen Remodel',
		recommendedSizes: [10, 15],
	},
	{
		value: 'bathroom-remodel',
		label: 'Bathroom Remodel',
		recommendedSizes: [10, 15],
	},
	{
		value: 'roof-tearoff',
		label: 'Roof Tear-Off',
		recommendedSizes: [20, 30],
	},
	{
		value: 'garage-cleanout',
		label: 'Garage Cleanout',
		recommendedSizes: [10, 15],
	},
	{
		value: 'yard-landscaping',
		label: 'Yard / Landscaping',
		recommendedSizes: [10, 15],
	},
	{
		value: 'construction-debris',
		label: 'Construction Debris',
		recommendedSizes: [20, 30],
	},
	{
		value: 'moving-downsizing',
		label: 'Moving / Downsizing',
		recommendedSizes: [15, 20],
	},
	{
		value: 'estate-cleanout',
		label: 'Estate Cleanout',
		recommendedSizes: [20, 30],
	},
] as const

export type ProjectTypeValue = (typeof PROJECT_TYPES)[number]['value']

// ── Project sizes (modifiers) ──

export const PROJECT_SIZES = [
	{ value: 'small', label: 'Small', sizeModifier: -1 },
	{ value: 'medium', label: 'Medium', sizeModifier: 0 },
	{ value: 'large', label: 'Large', sizeModifier: 1 },
] as const

export type ProjectSizeValue = (typeof PROJECT_SIZES)[number]['value']

// ── Debris types ──

export const DEBRIS_TYPES = [
	{
		value: 'mixed',
		label: 'Mixed Household',
		weightFactor: 1,
		warnings: [],
	},
	{
		value: 'heavy',
		label: 'Heavy (Concrete / Brick / Dirt)',
		weightFactor: 2,
		warnings: [
			'Heavy debris may reach weight limits before filling the dumpster. Consider a smaller size with a higher weight allowance.',
		],
	},
	{
		value: 'shingles',
		label: 'Shingles / Roofing',
		weightFactor: 1.8,
		warnings: [
			'Roofing shingles are very heavy. Most vendors limit shingle loads to 10-15 yard dumpsters.',
		],
	},
	{
		value: 'yard-waste',
		label: 'Yard Waste',
		weightFactor: 0.7,
		warnings: [],
	},
	{
		value: 'construction',
		label: 'Construction / Demo',
		weightFactor: 1.3,
		warnings: [],
	},
] as const

export type DebrisTypeValue = (typeof DEBRIS_TYPES)[number]['value']

// ── Dumpster sizes metadata ──

export const DUMPSTER_SIZES = [
	{
		value: 10,
		label: '10 Yard',
		cubicYards: 10,
		dimensions: "12' L x 8' W x 3.5' H",
		truckLoads: '3 pickup truck loads',
		bestFor: [
			'Small garage or basement cleanout',
			'Minor bathroom remodel',
			'Small landscaping project',
		],
		subcategorySlug: '10-yard-dumpster',
	},
	{
		value: 15,
		label: '15 Yard',
		cubicYards: 15,
		dimensions: "16' L x 8' W x 4' H",
		truckLoads: '4-5 pickup truck loads',
		bestFor: [
			'Medium home cleanout',
			'Kitchen remodel',
			'Garage cleanout with large items',
		],
		subcategorySlug: '15-yard-dumpster',
	},
	{
		value: 20,
		label: '20 Yard',
		cubicYards: 20,
		dimensions: "22' L x 8' W x 4.5' H",
		truckLoads: '6-8 pickup truck loads',
		bestFor: [
			'Whole-home cleanout',
			'Large remodel projects',
			'Roof tear-off (single layer)',
			'Moving / downsizing',
		],
		subcategorySlug: '20-yard-dumpster',
	},
	{
		value: 30,
		label: '30 Yard',
		cubicYards: 30,
		dimensions: "22' L x 8' W x 6' H",
		truckLoads: '9-12 pickup truck loads',
		bestFor: [
			'Large construction or demo projects',
			'Estate cleanout',
			'Major remodel or addition',
			'Commercial cleanout',
		],
		subcategorySlug: '30-yard-dumpster',
	},
	{
		value: 40,
		label: '40 Yard',
		cubicYards: 40,
		dimensions: "22' L x 8' W x 8' H",
		truckLoads: '12-16 pickup truck loads',
		bestFor: [
			'Large-scale construction',
			'Commercial demolition',
			'Major commercial cleanout',
			'Industrial projects',
		],
		subcategorySlug: '40-yard-dumpster',
	},
] as const

export type DumpsterSizeValue = (typeof DUMPSTER_SIZES)[number]['value']

/**
 * All dumpster subcategory slugs we recognize (superset of DUMPSTER_SIZES).
 * Sizes not in DUMPSTER_SIZES (5, 35) are mapped to the nearest standard size
 * for display purposes via matchListingToSize().
 */
export const ALL_DUMPSTER_SUBCATEGORY_SLUGS = [
	'5-yard-dumpster',
	'10-yard-dumpster',
	'15-yard-dumpster',
	'20-yard-dumpster',
	'30-yard-dumpster',
	'35-yard-dumpster',
	'40-yard-dumpster',
] as const

// ── Dumpster attributes (from RentalListing.attributes JSON) ──

export interface DumpsterAttributes {
	rentalPeriodDays?: number
}

export function parseDumpsterAttributes(
	attributes: unknown,
): DumpsterAttributes {
	if (!attributes || typeof attributes !== 'object') return {}
	const attrs = attributes as Record<string, unknown>
	return {
		rentalPeriodDays:
			typeof attrs.rentalPeriodDays === 'number'
				? attrs.rentalPeriodDays
				: undefined,
	}
}

// ── Non-standard sizes → nearest standard size mapping ──

const NONSTANDARD_SIZE_MAP: Record<string, string> = {
	'5-yard-dumpster': '10-yard-dumpster',
	'35-yard-dumpster': '30-yard-dumpster',
}

// ── Listing → size matching (subcategory + title fallback) ──

/**
 * Match a listing to the closest DUMPSTER_SIZES entry.
 * 1. Try subcategory slug exact match against standard sizes
 * 2. Try non-standard subcategory slugs (5-yard, 35-yard) → nearest standard
 * 3. Fallback: extract yard number from title and snap to nearest size
 * Returns the subcategorySlug or null if no match.
 */
export function matchListingToSize(listing: {
	title: string
	subcategories: Array<{ slug: string }>
}): string | null {
	// 1. Exact subcategory match against standard sizes
	for (const size of DUMPSTER_SIZES) {
		if (listing.subcategories.some((sc) => sc.slug === size.subcategorySlug)) {
			return size.subcategorySlug
		}
	}

	// 2. Non-standard subcategory sizes → map to nearest standard
	for (const sc of listing.subcategories) {
		const mapped = NONSTANDARD_SIZE_MAP[sc.slug]
		if (mapped) return mapped
	}

	// 3. Title-based fallback: extract the yard number
	const match = listing.title.match(/\b(\d+)\s*[-\s]?yard\b/i)
	if (match) {
		const yards = parseInt(match[1]!, 10)
		// Find closest size
		let closest: (typeof DUMPSTER_SIZES)[number] = DUMPSTER_SIZES[0]!
		let closestDiff = Math.abs(yards - closest.value)
		for (const size of DUMPSTER_SIZES) {
			const diff = Math.abs(yards - size.value)
			if (diff < closestDiff) {
				closest = size
				closestDiff = diff
			}
		}
		return closest.subcategorySlug
	}

	return null
}

// ── Recommendation engine ──

const SIZE_ORDER: DumpsterSizeValue[] = [10, 15, 20, 30, 40]

export interface Recommendation {
	primarySize: DumpsterSizeValue
	alternativeSmaller?: DumpsterSizeValue
	alternativeLarger?: DumpsterSizeValue
	explanation: string
	warnings: string[]
}

export function getRecommendation(
	projectType: ProjectTypeValue,
	projectSize: ProjectSizeValue,
	debrisType: DebrisTypeValue,
): Recommendation {
	const project = PROJECT_TYPES.find((p) => p.value === projectType)!
	const sizeMod = PROJECT_SIZES.find((s) => s.value === projectSize)!
	const debris = DEBRIS_TYPES.find((d) => d.value === debrisType)!

	// Pick base size from project recommendation (use [0] for small, [1] for large)
	const baseIndex = sizeMod.sizeModifier <= 0 ? 0 : 1
	let baseSize = project.recommendedSizes[
		Math.min(baseIndex, project.recommendedSizes.length - 1)
	] as DumpsterSizeValue

	// Apply size modifier for small projects (go down if possible)
	if (sizeMod.sizeModifier === -1) {
		const idx = SIZE_ORDER.indexOf(baseSize)
		if (idx > 0) baseSize = SIZE_ORDER[idx - 1]!
	}

	// For heavy debris, cap at 20 yards (weight limits make larger sizes impractical)
	if (debris.weightFactor >= 2 && baseSize > 20) {
		baseSize = 20
	}

	// For shingles, cap at 15 (most vendors limit shingle loads)
	if (debrisType === 'shingles' && baseSize > 15) {
		baseSize = 15
	}

	const primaryIdx = SIZE_ORDER.indexOf(baseSize)
	const alternativeSmaller =
		primaryIdx > 0 ? SIZE_ORDER[primaryIdx - 1] : undefined
	const alternativeLarger =
		primaryIdx < SIZE_ORDER.length - 1 ? SIZE_ORDER[primaryIdx + 1] : undefined

	const sizeInfo = DUMPSTER_SIZES.find((s) => s.value === baseSize)!
	const explanation = buildExplanation(project.label, sizeMod.label, sizeInfo)

	return {
		primarySize: baseSize,
		alternativeSmaller,
		alternativeLarger,
		explanation,
		warnings: [...debris.warnings],
	}
}

function buildExplanation(
	projectLabel: string,
	sizeLabel: string,
	sizeInfo: (typeof DUMPSTER_SIZES)[number],
): string {
	return `A ${sizeInfo.label.toLowerCase()} dumpster (${sizeInfo.cubicYards} cubic yards) is ideal for a ${sizeLabel.toLowerCase()} ${projectLabel.toLowerCase()} project. That's roughly ${sizeInfo.truckLoads}, with dimensions of ${sizeInfo.dimensions}.`
}
